import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import '../../styles/AddressForm.css';
import ControlledTextInputField from '../../component/ui/FormFields/ControlledTextInputField';
import CustomButton from '../../component/ui/FormFields/Button';
import {
  addBillingAddressRequest,
  resetBillingAddressState,
} from '../../redux/BillingAddress/BillingAddressAction';
import { BILLING_ADDRESS } from '../../component/common/Routes/AppRoutes';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { CUSTOMER_BILLING_ADDRESS } from '../../component/common/Routes/CustomerRoutes';
import {
  selectCityRequest,
  selectCountryRequest,
  selectStateRequest,
} from '../../redux/Common/selectorAction';

export default function AddAddressForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, success, error, address } = useSelector(
    (state) => state.billingAddress
  );
  const successMessage = address?.message;
  useEffect(() => {
    dispatch(resetBillingAddressState());
  }, []);
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();
  // address selector

  useEffect(() => {
    dispatch(selectCountryRequest());
  }, []);

  const formValue = watch();
  const countryId = formValue.country?.value;
  const stateId = formValue.state?.value;
  const countries = useSelector((state) => state.selector?.country?.data);
  // country code fetcher
  const countryForCode = countries?.map((value) => {
    return {
      label: ` +${value.phonecode} - ${value.short_name}`,
      value: value.phonecode,
    };
  });
  const states = useSelector((state) => state.selector?.state?.data);
  const cities = useSelector((state) => state.selector?.city?.data);

  const countryName = countries?.map((country) => {
    return { value: country?.id, label: country?.country_name };
  });
  const stateName = states?.map((state) => {
    return { value: state?.id, label: state?.state_name };
  });
  const cityName = cities?.map((city) => {
    return { value: city?.id, label: city?.city_name };
  });

  // useEffect runs whenever `country` changes
  useEffect(() => {
    setValue('state', '');
    setValue('city', '');
    dispatch(selectStateRequest(countryId));
  }, [countryId]);
  // change state
  useEffect(() => {
    setValue('city', '');
    dispatch(selectCityRequest(stateId));
  }, [stateId]);
  const fields = [
    {
      name: 'first_name',
      label: 'First Name *',
      type: 'text',
      rules: { required: 'First Name is required' },
      sm: 6,
      xs: 12,
    },
    {
      name: 'last_name',
      label: 'Last Name *',
      type: 'text',
      rules: { required: 'Last Name is required' },
      sm: 6,
      xs: 12,
    },
    {
      name: 'company_name',
      label: 'Company Name',
      type: 'text',
      sm: 6,
      xs: 12,
    },
    {
      name: 'address1',
      label: 'Address Line 1 *',
      type: 'text',
      rules: { required: 'Address Line 1 is required' },
      sm: 6,
      xs: 12,
    },
    { name: 'address2', label: 'Address Line 2', type: 'text', sm: 6, xs: 12 },
    {
      name: 'country',
      label: 'Country *',
      type: 'text',
      options: countryName,
      rules: { required: 'Country is required' },
      sm: 6,
      xs: 12,
    },

    {
      name: 'state',
      label: 'State *',
      type: 'text',
      options: stateName,
      rules: { required: 'State is required' },
      sm: 6,
      xs: 12,
    },
    {
      name: 'city',
      label: 'City *',
      type: 'text',
      options: cityName,
      rules: { required: 'City is required' },
      sm: 6,
      xs: 12,
    },

    {
      name: 'pincode',
      label: 'Pincode *',
      type: 'text',
      rules: {
        required: 'Pincode is required',
        pattern: {
          value: /^[0-9]+$/,
          message: 'Enter a valid numeric pincode',
        },
      },
      sm: 6,
      xs: 12,
    },
    {
      name: 'country_code',
      options: countryForCode,
      label: 'code',
      rules: {
        required: 'Code is Required',
      },
      sm: 2,
      xs: 4,
    },
    {
      name: 'phone',
      label: 'Phone Number *',
      type: 'text',
      rules: {
        required: 'Mobile Number is required',
        pattern: {
          value: /^\d+$/,
          message: 'Enter only numeric digits',
        },
      },
      sm: 4,
      xs: 8,
    },
    {
      name: 'emergency_country_code',
      options: countryForCode,
      label: 'country Code *',
      rules: {
        required: 'Code is Required',
      },
      sm: 2,
      xs: 4,
    },
    {
      name: 'emergency_contact',
      label: 'Emergency Contact',
      type: 'text',
      rules: {
        required: 'Mobile Number is required',
        pattern: {
          value: /^\d+$/,
          message: 'Enter only numeric digits',
        },
      },
      sm: 4,
      xs: 8,
    },
  ];
  useEffect(() => {
    if (error) {
      toast.error(`Error: ${error}`, {
        position: 'top-center',
        autoClose: 3000,
      });
    }
  }, [error]);

  useEffect(() => {
    if (successMessage) {
      toast.success('Billing Address Inserted Successfully', {
        position: 'top-center',
        autoClose: 3000,
        onClose: () => {
          dispatch(resetBillingAddressState());
          navigate(CUSTOMER_BILLING_ADDRESS);
        },
      });
    }
  }, [successMessage]);

  const onSubmit = (data) => {
    const formData = {
      ...data,
      city: data.city?.value,
      state: data.state?.value,
      country: data.country?.value,
      country_code: data?.country_code.value,
      emergency_country_code: data?.emergency_country_code.value,
    };

    dispatch(addBillingAddressRequest(formData));
  };
  return (
    <>
      <ToastContainer />
      <Box className='container'>
        <Card className='form-card'>
          <CardContent>
            <Typography
              variant='h5'
              component='h1'
              gutterBottom
              className='form-heading'
            >
              Add Address
            </Typography>
            <Divider className='form-divider' />
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={2}>
                {fields.map((field, index) => (
                  <Grid item xs={field.xs} sm={field.sm} key={index}>
                    <ControlledTextInputField
                      name={field.name}
                      control={control}
                      label={field.label}
                      placeholder={field.label}
                      rules={field.rules}
                      options={field.options}
                      type={field.type}
                      error={!!errors[field.name]} // Boolean indicating if there's an error
                      helperText={errors[field.name]?.message}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid item xs={12}>
                <Box className='button-container'>
                  <CustomButton
                    variant='contained'
                    className='cancel-button'
                    type='button'
                    text={'Cancel'}
                    onClick={() => navigate(CUSTOMER_BILLING_ADDRESS)}
                  />
                  <CustomButton
                    variant='contained'
                    className='success-button'
                    loading={loading}
                    type='submit'
                    text={'Submit'}
                  />
                </Box>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Box>
    </>
  );
}
